/**
 * How to render a staged row's source document (GDN-3874).
 *
 * The preview endpoint returns the file decoded as UTF-8 with
 * `errors="replace"`, so a binary file arrives as text whose every non-UTF-8
 * byte is U+FFFD. Rendering that as text is what put a scanned PDF's object
 * tree and binary noise on screen. Deciding the kind here, and refusing to
 * treat a lossy decode as text, is what stops it.
 */

/** What the modal should do with a document's body. */
export type DocumentKind = 'text' | 'pdf' | 'image' | 'binary';

/** Content types that are genuinely text even though they are not `text/*`. */
const TEXTISH = new Set([
  'application/json',
  'application/xml',
  'application/ld+json',
  'application/x-ndjson',
  'application/javascript',
  'application/sql',
]);

const REPLACEMENT = '�';

/**
 * When replacement characters mean "this was not text".
 *
 * Two tests, not one. The floor ignores the handful a genuine document can
 * carry, including the one the server's byte-limit cut can produce by
 * splitting a multi-byte character. The proportion stops that floor from
 * condemning a long document: three stray characters in a megabyte of clinical
 * text is mojibake, while a binary payload is mostly damage.
 */
const BINARY_REPLACEMENT_FLOOR = 3;
const BINARY_REPLACEMENT_RATIO = 0.001;

/** Bytes we inspect when the declared type is missing or useless. */
const HEAD_LENGTH = 1024;

const normalizeType = (contentType: string | undefined): string =>
  (contentType ?? '').split(';')[0]?.trim().toLowerCase() ?? '';

/**
 * Decide how to render a document from its declared type and its body.
 *
 * The magic bytes are checked before the declared type, not after it: a
 * migrated chart's recorded content type is whatever the source system wrote,
 * and a scan mislabelled as text is the case this exists for. A PDF's `%PDF-`
 * header is ASCII, so it survives the server's lossy decode intact.
 */
export function documentKind(
  contentType: string | undefined,
  text: string | undefined,
): DocumentKind {
  const body = text ?? '';
  const head = body.slice(0, HEAD_LENGTH);
  const type = normalizeType(contentType);

  // A PDF header is ASCII and anchored at byte zero, so it survives the decode
  // and cannot be confused with prose that merely mentions it.
  if (head.startsWith('%PDF-')) return 'pdf';
  if (type === 'application/pdf') return 'pdf';
  if (type.startsWith('image/')) return 'image';
  // PNG's first signature byte is lost to the decode; the three ASCII letters
  // that follow it are not, and they are anchored at the start.
  if (/^.?PNG\r?\n/.test(head)) return 'image';

  // Damage anywhere in the body means the server could not decode it, so it is
  // not text whatever the header claimed. Counted across the WHOLE body, not
  // just the head: a file can open with kilobytes of ASCII and turn binary
  // later, and inspecting only the start would send that tail to the screen —
  // the very bug this exists to stop.
  const damage = body.split(REPLACEMENT).length - 1;
  const damaged =
    damage >= BINARY_REPLACEMENT_FLOOR && damage / body.length > BINARY_REPLACEMENT_RATIO;

  // JPEG's marker is not ASCII, so it is recognised by an identifier a little
  // further in. That identifier is an ordinary English-ish word, so it is only
  // trusted where the type says nothing AND the body is visibly binary —
  // otherwise a note discussing Exif data would render as an image.
  if (damaged && (!type || type === 'application/octet-stream')) {
    const signature = body.slice(0, 64);
    if (signature.includes('JFIF') || signature.includes('Exif')) return 'image';
  }

  if (damaged) return 'binary';

  if (type.startsWith('text/') || TEXTISH.has(type)) return 'text';
  // No usable type and no damage: treat an empty body as nothing to show, and
  // anything else as the text it appears to be.
  if (!type || type === 'application/octet-stream') {
    return body ? 'text' : 'binary';
  }
  return 'binary';
}

/**
 * Whether a document URL is one this preview will follow.
 *
 * The value is server-generated today, so this is defence in depth for a
 * contract that is hand-authored and not yet deployed: a `javascript:` URL is
 * blocked by React in `src`, but not by `window.open`. `data:` is allowed
 * because the Storybook fixtures are data URLs.
 */
export function isSafeDocumentUrl(url: string | undefined): url is string {
  return typeof url === 'string' && (url.startsWith('https://') || url.startsWith('data:'));
}

/** Short operator-facing name for a content type, for the cannot-preview state. */
export function documentTypeLabel(contentType: string | undefined): string {
  const type = normalizeType(contentType);
  if (!type || type === 'application/octet-stream') return 'an unrecognised file type';
  return type;
}

/** Size of a document for the cannot-preview state. Binary units, as the server counts bytes. */
export function formatDocumentSize(bytes: number | undefined): string {
  if (!bytes) return '';
  const units = ['B', 'KiB', 'MiB', 'GiB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}
